import React, { useRef, useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import fx from 'glfx';
import { IoRepeat } from "react-icons/io5";
import { PiWarningLight } from 'react-icons/pi'
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

import { sendAnalyticsData } from '../utilities/analytics';

// ==========================================================================
// FACE FRAMING DETECTION (ported from main.js)
// ==========================================================================
// Checks whether the whole face sits inside the ".face-outline" overlay at a
// sensible size (not too far / too close / off-center), using MediaPipe
// FaceLandmarker's face-oval contour to build a bounding box for the face.

const FACE_LANDMARKER_WASM_PATH = '/models/wasm';
const FACE_LANDMARKER_MODEL_PATH = '/models/face_landmarker.task';

// ---- Distance/framing thresholds (with hysteresis) ----
const FACE_FIT_MIN_HEIGHT_RATIO = 0.76;
const FACE_FIT_MIN_HEIGHT_RATIO_EXIT = 0.76;
const FACE_FIT_OVERFLOW_TOLERANCE = 0.03;
const FACE_FIT_OVERFLOW_TOLERANCE_EXIT = 0.12;
const FACE_FIT_CENTER_TOLERANCE = 0.16;
const FACE_FIT_CENTER_TOLERANCE_EXIT = 0.22;

// Smoothing (EMA) to avoid per-frame landmark jitter flicker
const FACE_FIT_SMOOTHING_ALPHA = 0.35;

const FACE_DISTANCE_HINTS = {
  'no-face': 'Position your face within the guide',
  'too-far': 'Move a little closer',
  'too-close': 'Move back a little',
  'off-center': 'Center your face in the box',
  'good': 'Good! Please click the capture button'
};

// Standard MediaPipe FACEMESH_FACE_OVAL contour indices
const FACE_OVAL_INDICES = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379,
  378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109
];

function smoothMetric(prev, next) {
  return (prev === null || prev === undefined) ? next : prev + (next - prev) * FACE_FIT_SMOOTHING_ALPHA;
}

function smoothMetrics(store, raw) {
  if (!store) return { ...raw };
  const next = { ...store };
  Object.keys(raw).forEach(key => { next[key] = smoothMetric(next[key], raw[key]); });
  return next;
}

// Detects if the <video> is mirrored via CSS (e.g. transform: scaleX(-1))
function isVideoMirrored(videoEl) {
  const transform = window.getComputedStyle(videoEl).transform;
  if (!transform || transform === 'none') return false;
  const match = transform.match(/matrix\(([-0-9.,\s]+)\)/);
  if (!match) return false;
  const a = parseFloat(match[1].split(',')[0]);
  return a < 0;
}

// Maps a normalized MediaPipe landmark point (0..1, raw camera-frame space)
// to viewport pixel coordinates, matching how <video> is laid out on screen.
function mapVideoPointToViewport(videoEl, nx, ny) {
  const rect = videoEl.getBoundingClientRect();
  const vw = videoEl.videoWidth;
  const vh = videoEl.videoHeight;
  const objectFit = window.getComputedStyle(videoEl).objectFit || 'fill';
  const mirrored = isVideoMirrored(videoEl);

  let displayX, displayY;

  if (objectFit === 'cover' || objectFit === 'contain') {
    const scale = objectFit === 'cover'
      ? Math.max(rect.width / vw, rect.height / vh)
      : Math.min(rect.width / vw, rect.height / vh);

    const scaledW = vw * scale;
    const scaledH = vh * scale;
    const offsetX = (scaledW - rect.width) / 2;
    const offsetY = (scaledH - rect.height) / 2;

    displayX = nx * scaledW - offsetX;
    displayY = ny * scaledH - offsetY;
  } else {
    displayX = nx * rect.width;
    displayY = ny * rect.height;
  }

  if (mirrored) {
    displayX = rect.width - displayX;
  }

  return { x: rect.left + displayX, y: rect.top + displayY };
}

// Builds the whole-face bounding box (viewport pixel coords) from the
// face-oval contour landmarks.
function getFaceBoundingBox(videoEl, landmarks) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  FACE_OVAL_INDICES.forEach(idx => {
    const lm = landmarks[idx];
    if (!lm) return;
    const p = mapVideoPointToViewport(videoEl, lm.x, lm.y);
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  });

  return { left: minX, top: minY, right: maxX, bottom: maxY, width: maxX - minX, height: maxY - minY };
}

function computeFaceMetrics(faceBox, boxRect) {
  const heightRatio = faceBox.height / boxRect.height;
  const widthRatio = faceBox.width / boxRect.width;

  const faceCenterX = (faceBox.left + faceBox.right) / 2;
  const faceCenterY = (faceBox.top + faceBox.bottom) / 2;
  const boxCenterX = boxRect.left + boxRect.width / 2;
  const boxCenterY = boxRect.top + boxRect.height / 2;

  const centerOffsetX = Math.abs(faceCenterX - boxCenterX) / boxRect.width;
  const centerOffsetY = Math.abs(faceCenterY - boxCenterY) / boxRect.height;

  const overflowLeft = (boxRect.left - faceBox.left) / boxRect.width;
  const overflowRight = (faceBox.right - boxRect.right) / boxRect.width;
  const overflowTop = (boxRect.top - faceBox.top) / boxRect.height;
  const overflowBottom = (faceBox.bottom - boxRect.bottom) / boxRect.height;
  const overflowMargin = Math.max(overflowLeft, overflowRight, overflowTop, overflowBottom);

  return { heightRatio, widthRatio, centerOffsetX, centerOffsetY, overflowMargin };
}

function evaluateFaceFit(metrics, wasGood) {
  const overflowLimit = wasGood ? FACE_FIT_OVERFLOW_TOLERANCE_EXIT : FACE_FIT_OVERFLOW_TOLERANCE;
  if (metrics.overflowMargin > overflowLimit) return 'too-close';

  const minSizeRatio = wasGood ? FACE_FIT_MIN_HEIGHT_RATIO_EXIT : FACE_FIT_MIN_HEIGHT_RATIO;
  if (metrics.heightRatio < minSizeRatio) return 'too-far';

  const centerLimit = wasGood ? FACE_FIT_CENTER_TOLERANCE_EXIT : FACE_FIT_CENTER_TOLERANCE;
  if (metrics.centerOffsetX > centerLimit || metrics.centerOffsetY > centerLimit) return 'off-center';

  return 'good';
}

const Camera = (props, {hint}) => {

  const [initCamera, setInitCamera] = useState(false);
  const [cameraAccessBlocked, setCameraAccessBlocked] = useState(false);
  const [lightingDetector, setLightingDetector] = useState('');
  const [imageSrc, setImageSrc] = useState(null);
  const [imageCroppedSrc, setImageCroppedSrc] = useState(null);
  const [resolution, setResolution] = useState({width: 1280, height: 1280})
  const [timerStart, setTimerStart] = useState(false);
  const [facingMode, setFacingMode] = useState("user");
  const [lightingValue, setLightingValue] = useState(0);

  const [progress, setProgress] = useState(0);
  const [currentTextIndex, setCurrentTextIndex] = useState(4);
  const texts = ["Scanning lips completed", "Scanning nose completed", "Scanning eye completed", "Scanning hair completed", "Don't move..."];

  // Face framing (zoom in/out) detection — is the face inside ".face-outline"?
  const [faceDistanceState, setFaceDistanceState] = useState('no-face');
  const faceDistanceStateRef = useRef('no-face'); // mirrors state for use inside the rAF loop
  const faceDistanceGoodSinceRef = useRef(null);
  const faceLandmarkerRef = useRef(null);
  const faceLandmarkerLoadPromiseRef = useRef(null);
  const faceDistanceLoopIdRef = useRef(null);
  const smoothedFaceMetricsRef = useRef(null);

  const FACE_DISTANCE_HOLD_MS = 500; // how long "good" must hold before enabling capture

  const webcamRef = useRef(null);
  const outputCanvasRef = useRef(null);  // existing
  const faceOutlineRef = useRef(null);   // attach to <img className="face-outline"> — also the framing box
  
  const { type } = useParams();
  const [chosenDeviceId, setChosenDeviceId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // videoConstraints is computed each render and will reflect chosenDeviceId and facingMode
  const videoConstraints = chosenDeviceId ? {
    deviceId: { exact: chosenDeviceId },
    width: { ideal: 1280 },
    height: { ideal: 1280 },
    facingMode: { ideal: facingMode }
  } : {
    width: { ideal: 1280 },
    height: { ideal: 1280 },
    facingMode: { ideal: facingMode }
  };

  useEffect(() => {
    if(!location.state){
      navigate('/');
    }

  }, [location.state])

  const switchCamera = () => {
    if (facingMode == 'user') {
      setFacingMode('environment')
      document.querySelector('.video') && document.querySelector('.video').classList.add("env-mode");
    } else {
      setFacingMode('user')
      document.querySelector('.video') && document.querySelector('.video').classList.remove("env-mode");
    }
    // Clear any previously chosen deviceId because facingMode may not match deviceId
    setChosenDeviceId(null);
    // re-check camera to ensure constraints are applied after switching
    checkCameraAccess();
  }
  
  const startCapture = () => {
    sendAnalyticsData('Click','Photo Button')
    setTimerStart(true)
  }

  // ==========================================================================
  // Face framing loop (is the face inside the ".face-outline" box, zoomed in/out correctly?)
  // ==========================================================================
  const loadFaceLandmarker = async () => {
    if (faceLandmarkerRef.current) return faceLandmarkerRef.current;
    if (faceLandmarkerLoadPromiseRef.current) return faceLandmarkerLoadPromiseRef.current;

    faceLandmarkerLoadPromiseRef.current = (async () => {
      const filesetResolver = await FilesetResolver.forVisionTasks(FACE_LANDMARKER_WASM_PATH);

      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(filesetResolver, {
        baseOptions: {
          modelAssetPath: FACE_LANDMARKER_MODEL_PATH,
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        numFaces: 1
      });

      return faceLandmarkerRef.current;
    })();

    return faceLandmarkerLoadPromiseRef.current;
  };

  const updateFaceDistanceUI = (state) => {
    if (state === faceDistanceStateRef.current) return;
    faceDistanceStateRef.current = state;

    if (state === 'good') {
      if (faceDistanceGoodSinceRef.current === null) faceDistanceGoodSinceRef.current = Date.now();
    } else {
      faceDistanceGoodSinceRef.current = null;
    }

    setFaceDistanceState(state);
  };

  // true once the state has been "good" continuously for FACE_DISTANCE_HOLD_MS
  // (fails open if the model never loaded, e.g. offline / blocked, so scanning
  // still works without the extra check).
  const isFaceDistanceOk = () => {
    if (!faceLandmarkerRef.current) return true;
    if (faceDistanceStateRef.current !== 'good') return false;
    if (faceDistanceGoodSinceRef.current === null) return false;
    return (Date.now() - faceDistanceGoodSinceRef.current) >= FACE_DISTANCE_HOLD_MS;
  };

  const faceDistanceLoop = () => {
    const video = webcamRef.current?.video;
    const boxEl = faceOutlineRef.current;

    if (!video || video.readyState < 2 || !video.videoWidth || !faceLandmarkerRef.current) {
      faceDistanceLoopIdRef.current = requestAnimationFrame(faceDistanceLoop);
      return;
    }

    const nowMs = performance.now();
    const results = faceLandmarkerRef.current.detectForVideo(video, nowMs);

    if (results.faceLandmarks && results.faceLandmarks.length > 0) {
      const landmarks = results.faceLandmarks[0];
      const wasGood = faceDistanceStateRef.current === 'good';

      const boxRect = boxEl ? boxEl.getBoundingClientRect() : null;

      if (!boxEl || !boxRect.width || !boxRect.height) {
        updateFaceDistanceUI('good');
      } else {
        const faceBox = getFaceBoundingBox(video, landmarks);
        smoothedFaceMetricsRef.current = smoothMetrics(smoothedFaceMetricsRef.current, computeFaceMetrics(faceBox, boxRect));
        updateFaceDistanceUI(evaluateFaceFit(smoothedFaceMetricsRef.current, wasGood));
      }
    } else {
      smoothedFaceMetricsRef.current = null;
      updateFaceDistanceUI('no-face');
    }

    faceDistanceLoopIdRef.current = requestAnimationFrame(faceDistanceLoop);
  };

  const startFaceDistanceLoop = () => {
    if (faceDistanceLoopIdRef.current !== null) return; // already running
    faceDistanceStateRef.current = 'no-face';
    setFaceDistanceState('no-face');
    faceDistanceGoodSinceRef.current = null;
    smoothedFaceMetricsRef.current = null;
    faceDistanceLoopIdRef.current = requestAnimationFrame(faceDistanceLoop);
  };

  const stopFaceDistanceLoop = () => {
    if (faceDistanceLoopIdRef.current !== null) {
      cancelAnimationFrame(faceDistanceLoopIdRef.current);
      faceDistanceLoopIdRef.current = null;
    }
    faceDistanceStateRef.current = 'no-face';
    setFaceDistanceState('no-face');
    faceDistanceGoodSinceRef.current = null;
    smoothedFaceMetricsRef.current = null;
  };

  // Kick off / tear down the face pose+framing loop alongside the camera
  useEffect(() => {
    if (initCamera) {
      loadFaceLandmarker()
        .then(() => startFaceDistanceLoop())
        .catch(err => console.warn('Face distance detection unavailable:', err));
    }
    return () => {
      stopFaceDistanceLoop();
    };
  }, [initCamera]);

  // Lighting check and frame processing
const checkLighting = async (opts = {}) => {
  const { debug = false, alphaThreshold = 10 } = opts;

  let minBrightness = 80;
  
  if (document.body.clientWidth > 1024 && document.body.clientHeight > 1366) { // led
    minBrightness = 0;
  }else if(document.body.clientWidth <= 480){ //mobile
    // minBrightness = 130;
    minBrightness = 120;
  }

  // grab elements
  const video = webcamRef.current?.video || document.querySelector('.video');
  const faceEl = faceOutlineRef.current || document.querySelector('.face-outline');
  if (!video || !faceEl) return;

  // ensure video pixel size available
  const videoFrameWidth = video.videoWidth || resolution.width;
  const videoFrameHeight = video.videoHeight || resolution.height;
  if (!videoFrameWidth || !videoFrameHeight) return;

  // DOM boxes (accounts for transforms)
  const videoRect = video.getBoundingClientRect();
  const faceRect = faceEl.getBoundingClientRect();
  if (faceRect.width === 0 || faceRect.height === 0) return;

  // map DOM -> video source pixel coordinates
  const scaleX = videoFrameWidth / videoRect.width;
  const scaleY = videoFrameHeight / videoRect.height;

  // relative position of face overlay inside video (DOM px)
  const relX = faceRect.left - videoRect.left;
  const relY = faceRect.top - videoRect.top;

  // optional Y offset in DOM pixels (preserve your device-specific offsets)
  let yOffset = 20;
  if (document.body.offsetWidth === 720 && document.body.offsetHeight <= 1192) {
    yOffset = 232;
  }

  // compute source rectangle in video pixels
  const srcX = Math.round(relX * scaleX);
  const srcY = Math.round((relY + yOffset) * scaleY);
  const srcW = Math.round(faceRect.width * scaleX);
  const srcH = Math.round(faceRect.height * scaleY);

  // clamp to video frame
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const sX = clamp(srcX, 0, videoFrameWidth - 1);
  const sY = clamp(srcY, 0, videoFrameHeight - 1);
  const sW = clamp(srcW, 1, videoFrameWidth - sX);
  const sH = clamp(srcH, 1, videoFrameHeight - sY);

  // draw the video crop into canvas
  const vCanvas = document.createElement('canvas');
  vCanvas.width = sW;
  vCanvas.height = sH;
  const vCtx = vCanvas.getContext('2d');

  try {
    // drawImage(video, sx, sy, sw, sh, dx, dy, dw, dh)
    vCtx.drawImage(video, sX, sY, sW, sH, 0, 0, sW, sH);
  } catch (e) {
    // sometimes video frame isn't ready
    return;
  }

  // build mask canvas the exact same pixel size as the video crop
  const mCanvas = document.createElement('canvas');
  mCanvas.width = sW;
  mCanvas.height = sH;
  const mCtx = mCanvas.getContext('2d');

  // draw the face-outline into mask canvas so mask pixels align exactly with vCanvas
  // If faceEl is an <img>, draw it directly scaled to sW x sH (best alignment).
  if (faceEl.tagName === 'IMG') {
    // If the image isn't loaded yet, wait
    if (!faceEl.complete) {
      await new Promise((res) => { faceEl.onload = res; faceEl.onerror = res; });
    }
    // Draw the image scaled to the crop size (aligns with faceRect scale)
    mCtx.clearRect(0, 0, sW, sH);
    mCtx.drawImage(faceEl, 0, 0, faceEl.naturalWidth, faceEl.naturalHeight, 0, 0, sW, sH);
  } else {
    // fallback: read computed background-image url
    const bg = getComputedStyle(faceEl).backgroundImage;
    const urlMatch = bg && bg.match(/url\(["']?(.*?)["']?\)/);
    if (!urlMatch) {
      // no mask source — fallback to bounding-box brightness
      const frame = vCtx.getImageData(0, 0, sW, sH);
      computeBrightnessFromImageData(frame, minBrightness);
      return;
    }
    const maskImg = new Image();
    maskImg.crossOrigin = 'anonymous';
    maskImg.src = urlMatch[1];
    await new Promise((res) => { maskImg.onload = res; maskImg.onerror = res; });
    mCtx.drawImage(maskImg, 0, 0, maskImg.width, maskImg.height, 0, 0, sW, sH);
  }

  if (debug) {
    // attach debug canvases so you can see alignment visually
    vCanvas.style.position = 'fixed';
    vCanvas.style.right = '10px';
    vCanvas.style.bottom = '10px';
    vCanvas.style.border = '2px solid lime';
    vCanvas.style.zIndex = '999999';
    mCanvas.style.position = 'fixed';
    mCanvas.style.right = (20 + sW) + 'px';
    mCanvas.style.bottom = '10px';
    mCanvas.style.border = '2px solid red';
    mCanvas.style.zIndex = '999999';
    document.body.appendChild(vCanvas);
    document.body.appendChild(mCanvas);
    // remove after a while
    setTimeout(() => { try { vCanvas.remove(); mCanvas.remove(); } catch {} }, 5000);
  }

  // get image data and compute brightness using only mask alpha
  const videoData = vCtx.getImageData(0, 0, sW, sH);
  const maskData = mCtx.getImageData(0, 0, sW, sH);

  let totalBrightness = 0;
  let count = 0;
  for (let i = 0; i < videoData.data.length; i += 4) {
    const alpha = maskData.data[i + 3];
    if (alpha > alphaThreshold) {
      const r = videoData.data[i];
      const g = videoData.data[i + 1];
      const b = videoData.data[i + 2];
      totalBrightness += (r + g + b) / 3;
      count++;
    }
  }

  const avgBrightness = count > 0 ? totalBrightness / count : 255;
  if (avgBrightness < minBrightness){
    setLightingDetector('Lighting too dark');
  }else{
    setLightingDetector('');
  }
  setLightingValue(avgBrightness);

  // helper: fallback bounding-box computation (kept internal)
  function computeBrightnessFromImageData(imgData, thresholdBrightness) {
    let t = 0;
    for (let i = 0; i < imgData.data.length; i += 4) {
      t += (imgData.data[i] + imgData.data[i + 1] + imgData.data[i + 2]) / 3;
    }
    const avg = t / (imgData.data.length / 4);
    if (avg < thresholdBrightness){
      setLightingDetector('Lighting too dark');
    }else{
      setLightingDetector('');
    }
    setLightingValue(avg);
  }
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const stopExistingStreams = () => {
  try {
    // stop tracks from the react-webcam element if present
    const v = webcamRef.current?.video;
    if (v && v.srcObject) {
      const tracks = v.srcObject.getTracks();
      tracks.forEach((t) => {
        try { t.stop(); } catch(e) {}
      });
      v.srcObject = null;
    }

    // also attempt to stop any global streams that may be hanging
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      // cannot access previous streams directly, but we can enumerate streams left on elements
      // above we already attempted to stop srcObject tracks.
    }
  } catch (e) {
    console.warn('stopExistingStreams error', e);
  }
};

const checkCameraAccess = async () => {
  try {
    // Ensure any previous streams are stopped before probing
    stopExistingStreams();

    // initial permissive probe (will trigger permission prompt on many browsers)
    try {
      const probeStream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 720 }, height: { ideal: 720 }, facingMode: { ideal: facingMode } },
        audio: false,
      });

      // got stream -> we can use it to determine actual settings
      const track = probeStream.getVideoTracks()[0];
      const settings = track.getSettings();
      const smaller = Math.min(settings.width || 720, settings.height || 720);

      let target = 720;
      if (smaller >= 1280) target = 1280;
      else if (smaller >= 720) target = 720;
      else target = Math.max(360, Math.floor(smaller / 2));

      setResolution({ width: target, height: target });

      // stop probe tracks immediately to free camera
      probeStream.getTracks().forEach((t) => t.stop());

      setInitCamera(true);
      setCameraAccessBlocked(false);
      return;
    } catch (err) {
      console.warn("Initial getUserMedia probe failed:", err.name, err.message);
      // If NotReadableError, attempt a quick retry loop (some Android devices recover quickly)
      if (err && err.name === 'NotReadableError') {
        let recovered = false;
        for (let i = 1; i <= 3; i++) {
          await sleep(300 * i); // small exponential-ish backoff
          try {
            // attempt low-res probe (less likely to be blocked)
            const tryStream = await navigator.mediaDevices.getUserMedia({
              video: { width: { ideal: 320 }, height: { ideal: 240 }, facingMode: { ideal: facingMode } },
              audio: false,
            });
            // success
            tryStream.getTracks().forEach((t) => t.stop());
            setResolution({ width: 720, height: 720 });
            setInitCamera(true);
            setCameraAccessBlocked(false);
            recovered = true;
            break;
          } catch (retryErr) {
            console.warn(`Retry ${i} failed:`, retryErr.name, retryErr.message);
            // continue retrying
          }
        }
        if (recovered) return;
        // otherwise fallthrough to device enumeration below
      }
      // else fallthrough to enumeration fallback
    }

    // --- enumerate devices fallback ---
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      throw new Error('enumerateDevices not supported');
    }

    // Attempt a quick low-res getUserMedia to allow labels on some browsers
    try {
      const quick = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 320 }, height: { ideal: 240 } }, audio: false });
      quick.getTracks().forEach((t) => t.stop());
    } catch (e) {
      console.log("quick probe before enumerate failed (permission/deny or NotReadable):", e.name, e.message);
      // continue to enumerateDevices anyway
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const cams = devices.filter((d) => d.kind === "videoinput");
    console.log("enumerated cams:", cams);

    // try each deviceId sequentially with permissive constraints
    for (const cam of cams) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: cam.deviceId }, width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false
        });
        stream.getTracks().forEach((t) => t.stop());
        // success using this device
        setChosenDeviceId(cam.deviceId);
        setResolution({ width: 720, height: 720 });
        setInitCamera(true);
        setCameraAccessBlocked(false);
        return;
      } catch (e) {
        console.warn("deviceId probe failed for", cam.deviceId, e.name, e.message);
        // try next device
      }
    }

    // final attempt: facingMode with minimal constraints
    try {
      const finalAttempt = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: facingMode }, width: { ideal: 320 }, height: { ideal: 240 } }, audio: false });
      finalAttempt.getTracks().forEach((t) => t.stop());
      setResolution({ width: 720, height: 720 });
      setInitCamera(true);
      setCameraAccessBlocked(false);
      return;
    } catch (finalErr) {
      console.error("Final fallback failed", finalErr.name, finalErr.message);
      handleCameraError(finalErr);
      setCameraAccessBlocked(true);
      return;
    }

  } catch (err) {
    console.error("camera error", err);
    handleCameraError(err);
    setCameraAccessBlocked(true);
  }
};

// -- Add this useEffect (near your other useEffects) to re-probe when device list changes or tab becomes visible --
useEffect(() => {
  const onDeviceChange = () => {
    // re-check when devices change (camera plugged/unplugged or permission changed)
    console.log('media device change detected, rechecking camera');
    checkCameraAccess();
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      // tab regained focus, re-check in case camera was taken by other app
      console.log('visibility restored, rechecking camera');
      checkCameraAccess();
    }
  };

  navigator.mediaDevices && navigator.mediaDevices.addEventListener && navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    navigator.mediaDevices && navigator.mediaDevices.removeEventListener && navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
    document.removeEventListener('visibilitychange', onVisibility);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const handleCameraError = (error) => {
    switch (error.name) {
      case 'NotAllowedError':
        sendAnalyticsData('Click', 'Not Allowed Camera');
        setCameraAccessBlocked(true);
        break;
      case 'NotFoundError':
        console.error("No camera devices found.");
        setCameraAccessBlocked(true);
        break;
      case 'NotReadableError':
        console.error("Camera is currently unavailable (possibly in use by another application).");
        setCameraAccessBlocked(true);
        break;
      case 'OverconstrainedError':
        console.error("The specified camera constraints cannot be met.");
        setCameraAccessBlocked(true);
        break;
      case 'SecurityError':
        console.error("Security policy prevented access to the camera.");
        setCameraAccessBlocked(true);
        break;
      default:
        // Catch-all for other camera-related errors
        console.error("An unknown error occurred while accessing the camera:", error);
        setCameraAccessBlocked(true);
        break;
    }
  };
  
  useEffect(() => {
    if(initCamera){
      const lightingInterval = setInterval(checkLighting, 100);
      return () => clearInterval(lightingInterval);
    }
  }, [initCamera])

  useEffect(() => {
    let interval;

    if (timerStart) {
      interval = setInterval(() => {
        setCurrentTextIndex((prevTimerText) => {
          if (prevTimerText === 0) {
            clearInterval(interval); // Clear the interval when timerText reaches zero

            return 0;
          } else {
            return prevTimerText - 1;
          }
        });
      // }, 1000*2.3);
      }, 1000*2.3);
    }

    return () => {
      clearInterval(interval); // Clear the interval when the component unmounts
    };

    
  }, [timerStart]);

  useEffect(() => {
    let progressInterval;
  
    if (timerStart) {
      if (progress < 100) {
        progressInterval = setInterval(() => {
          setProgress((prev) => Math.min(prev + 1, 100));
        }, 100);
      } else {
        captureImage();
      }
    }
  
    return () => {
      clearInterval(progressInterval);
    };
  }, [progress, timerStart]);
  

  useEffect(() => {
    checkCameraAccess();
    // cleanup: stop any active webcam tracks when unmounting
    return () => {
      try {
        const v = webcamRef.current?.video;
        if (v && v.srcObject) {
          const tracks = v.srcObject.getTracks();
          tracks.forEach((t) => t.stop());
        }
      } catch (e) {
        // ignore
      }
      stopFaceDistanceLoop();
      try {
        faceLandmarkerRef.current?.close();
      } catch (e) {
        // ignore
      }
      faceLandmarkerRef.current = null;
      faceLandmarkerLoadPromiseRef.current = null;
    }
  }, []);

function mapRectToVideoPixels(videoEl, rect) {
  const videoRect = videoEl.getBoundingClientRect();

  const videoW = videoEl.videoWidth;
  const videoH = videoEl.videoHeight;

  const displayW = videoRect.width;
  const displayH = videoRect.height;

  const videoAspect = videoW / videoH;
  const displayAspect = displayW / displayH;

  let drawW, drawH, offsetX, offsetY;

  // Assume object-fit: cover
  if (displayAspect > videoAspect) {
    drawH = displayH;
    drawW = drawH * videoAspect;
    offsetX = (displayW - drawW) / 2;
    offsetY = 0;
  } else {
    drawW = displayW;
    drawH = drawW / videoAspect;
    offsetX = 0;
    offsetY = (displayH - drawH) / 2;
  }

  const scaleX = videoW / drawW;
  const scaleY = videoH / drawH;

  let x = (rect.left - videoRect.left - offsetX) * scaleX;
  let y = (rect.top - videoRect.top - offsetY) * scaleY;
  let w = rect.width * scaleX;
  let h = rect.height * scaleY;

  // clamp to video bounds
  x = Math.max(0, x);
  y = Math.max(0, y);
  if (x + w > videoW) w = videoW - x;
  if (y + h > videoH) h = videoH - y;

  return {
    x: Math.round(x),
    y: Math.round(y),
    w: Math.round(w),
    h: Math.round(h)
  };
}

const captureImage = async () => {
  setTimerStart(false);

  const videoEl = webcamRef.current?.video;
  const outlineEl = faceOutlineRef.current;
  const outputCanvas = outputCanvasRef.current;

  if (!videoEl || !outlineEl) return;
  if (videoEl.readyState < 2) return;

  // Safety net: if pose/framing regressed since the timer started, bail out
  // and let the loop's hint text / gated button prompt the user to retry.
  if (!isFaceDistanceOk()) return;

  const vidW = videoEl.videoWidth;
  const vidH = videoEl.videoHeight;
  if (!vidW || !vidH) return;

  /* =================================================
     1) CAPTURE FULL FRAME (NATIVE CAMERA RESOLUTION)
     ================================================= */
  const fullCanvas = document.createElement('canvas');
  fullCanvas.width = vidW;
  fullCanvas.height = vidH;

  const fullCtx = fullCanvas.getContext('2d');

  const transform = getComputedStyle(videoEl).transform || '';
  const isMirrored =
    transform.includes('scaleX(-1)') ||
    transform.includes('matrix(-1');

  if (isMirrored) {
    fullCtx.save();
    fullCtx.translate(vidW, 0);
    fullCtx.scale(-1, 1);
    fullCtx.drawImage(videoEl, 0, 0, vidW, vidH);
    fullCtx.restore();
  } else {
    fullCtx.drawImage(videoEl, 0, 0, vidW, vidH);
  }

  /* ✅ FULL IMAGE BASE64 */
  setImageSrc(fullCanvas.toDataURL('image/jpeg', 0.92));

  /* =================================================
     2) MAP .face-outline → REAL VIDEO PIXELS
     (object-fit: cover safe)
     ================================================= */
  const videoRect = videoEl.getBoundingClientRect();
  const outlineRect = outlineEl.getBoundingClientRect();

  const videoAR = vidW / vidH;
  const domAR = videoRect.width / videoRect.height;

  let renderW, renderH, offsetX, offsetY;

  // object-fit: cover math
  if (videoAR > domAR) {
    renderH = videoRect.height;
    renderW = renderH * videoAR;
    offsetX = (renderW - videoRect.width) / 2;
    offsetY = 0;
  } else {
    renderW = videoRect.width;
    renderH = renderW / videoAR;
    offsetX = 0;
    offsetY = (renderH - videoRect.height) / 2;
  }

  const scaleX = vidW / renderW;
  const scaleY = vidH / renderH;

  let sx = (outlineRect.left - videoRect.left + offsetX) * scaleX;
  let sy = (outlineRect.top - videoRect.top + offsetY) * scaleY;
  let sw = outlineRect.width * scaleX;
  let sh = outlineRect.height * scaleY;

  // Optional padding around face
  const padding = 0.15;
  sx -= sw * padding;
  sy -= sh * padding;
  sw += sw * padding * 2;
  sh += sh * padding * 2;

  // Clamp to video bounds
  sx = Math.max(0, sx);
  sy = Math.max(0, sy);
  if (sx + sw > vidW) sw = vidW - sx;
  if (sy + sh > vidH) sh = vidH - sy;

  if (sw <= 0 || sh <= 0) return;

  /* =================================================
    3) HIGH-RES CROP (DPR APPLIED ONLY HERE)
    ================================================= */
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = Math.round(sw * dpr);
  cropCanvas.height = Math.round(sh * dpr);

  const cropCtx = cropCanvas.getContext('2d');
  cropCtx.imageSmoothingEnabled = true;
  cropCtx.imageSmoothingQuality = 'high';

  cropCtx.drawImage(
    fullCanvas,
    sx, sy, sw, sh,
    0, 0, sw * dpr, sh * dpr
  );

  // Optional debug preview
  if (outputCanvas) {
    outputCanvas.width = sw;
    outputCanvas.height = sh;
    outputCanvas
      .getContext('2d')
      .drawImage(cropCanvas, 0, 0, sw, sh);
  }

  setImageCroppedSrc(fullCanvas.toDataURL('image/jpeg', 0.92))

  sendAnalyticsData?.('Click', 'Photo Taken');
  setInitCamera?.(false);
};

  const retakeEvent = () => {
    setTimerStart(false)
    checkCameraAccess();
    sendAnalyticsData('Click','Retake Photo')
    setImageSrc(null);
    setImageCroppedSrc(null)
    setTimerStart(false);
    setCurrentTextIndex(4);
    setProgress(0);
  }

  useEffect(() => {
    if(lightingDetector){
      setTimerStart(false)
      setCurrentTextIndex(4);
      setProgress(0);
    }
  })

  return (
    <>
      {
      !imageSrc ?
        <div className={timerStart ? 'camera-wrapper d-flex justify-content-center align-items-center scanning' : 'camera-wrapper d-flex justify-content-center align-items-center'}>
          {props.children}
          <img
            ref={faceOutlineRef}
            className={`face-outline ${timerStart ? 'opacity-0' : ''} ${faceDistanceState === 'good' ? 'distance-good' : 'distance-warning'}`}
            src="/images/face-outline.png"
            alt="face outline"
          />

          <img
            className={`face-bg ${!timerStart ? 'opacity-0' : ''}`}
            src="/images/bg-grid.png"
            alt="face bg"
          />

          {timerStart &&
            (
              <div className="gimmick-scanner">
                <div className="scanner-text">
                  <p className="fade-text">{texts[currentTextIndex]}</p>
                </div>
                <div className="progress-bar">
                  <div className="progress" style={{ width: `${progress}%` }}></div>
                </div>
              </div>
            )}

          {/* <div className="lighting-detect">Avg. Lighting <br/> {brightnessValue.toFixed(0)}</div> */}
          
          {cameraAccessBlocked ? (
            <p className='camera-blocked'>
              <span><PiWarningLight/></span>
              Camera not found or it access is blocked, Please allow camera access in your browser settings.
              <a className='btn d-table mx-auto btn-secondary mt-3 font-small' onClick={()=>{checkCameraAccess()}}>Retry</a>
            </p>
          ) : 
            initCamera ? 
            (
              <>
                <Webcam
                  className="video"
                  audio={false}
                  muted
                  ref={webcamRef}
                  mirrored={true}
                  screenshotFormat="image/jpeg"
                  minScreenshotWidth={resolution.width}
                  minScreenshotHeight={resolution.height}
                  videoConstraints={videoConstraints}
                  // add playsInline to avoid fullscreen on some mobile browsers
                  playsInline
                  // react-webcam passes props to underlying video element
                  // you can also pass onUserMediaError and onUserMedia to debug
                  onUserMediaError={(e) => {
                    console.error("Webcam onUserMediaError", e);
                    handleCameraError(e);
                  }}
                  onUserMedia={() => {
                    console.log("Webcam ready");
                    sendAnalyticsData('Info', 'Camera Stream Started');
                  }}
                />
                
                <canvas ref={outputCanvasRef} id="output"></canvas>

                {!timerStart && (
                  <>
                    <div className="lighting-detect">{lightingDetector}</div>
                    <div className="lighting-detect brightness-meter">Brightness<br/><span  style={{'top': '49%', 'fontSize': '12px'}}>{lightingValue.toFixed(0)}</span></div>
                    <div className={`lighting-detect face-framing-hint ${faceDistanceState === 'good' ? 'is-good' : ''}`}>{FACE_DISTANCE_HINTS[faceDistanceState] || FACE_DISTANCE_HINTS['no-face']}</div>
                  </>
                )}
              </>
            )
            :
              null
          }
          <div className='footer-bar-wrapper'>
            <div className='position-relative w-100' style={{'height': '64px'}}>
              {/* <div className='camera-bottom-text d-table mx-auto'>Please align your face in the oval above</div> */}
              {
              !timerStart && !cameraAccessBlocked && lightingDetector === '' && faceDistanceState === 'good'?
              <>
                <div onClick={startCapture} className="capture-button capture-button-bordered d-flex justify-content-center align-items-center"></div>
                <div onClick={switchCamera} style={{left: '90%'}} className={`capture-button capture-switch bg-transparent d-flex justify-content-center align-items-center`}>
                  <IoRepeat style={{fontSize: '28px', 'color': "#ffffff"}}/>
                </div>
              </>
              :
              null
              }
            </div>
            <div className='footer-bar-text'>
              POSITION YOUR FACE<br/>
              WITHIN THE GUIDE<br/>
              FOR ACCURATE ANALYSIS<br/>
            </div>
          </div>
        </div>
        :
        <div className='captured-wrapper'>
          <div className='captured-wrapper-inner'>
            <img className='image-captured' src={imageSrc} alt="Captured" />
          </div>
          <div className='captured-action d-flex'>
            <div className='footer-bar-text w-50 p-0' style={{'backgroundColor': 'transparent'}}>
              POSITION YOUR FACE<br/>
              WITHIN THE GUIDE<br/>
              FOR ACCURATE ANALYSIS<br/>
            </div>
            <div className='w-50 button-captured'>
              <a className='btn btn-secondary d-table ms-auto mb-2 mb-md-3 w-100' onClick={retakeEvent}>
                Retake
              </a>
              {/* <Link className='btn btn-primary d-table ms-auto w-100' onClick={() => {sendAnalyticsData('Click','Go To SkinTone Page')}} to={`/${type}/result`} state={{photo: brighterCanvas ? canvasVideo : imageSrc}}>Next</Link> */}
              <Link className='btn btn-primary d-table ms-auto w-100' onClick={() => {sendAnalyticsData('Click','Go To SkinTone Page')}} to={`/${type}/result`} state={{photo: imageCroppedSrc, name: location?.state?.name, email: location?.state?.email, phone: location?.state?.phone, qrCode: location?.state?.qrCode}}>Next</Link>
            </div>
          </div>
        </div>
      }
    </>
  );
};

export default Camera;